import React, { createContext, useContext, useState, useCallback } from 'react';

/**
 * ------------------------------------------------------------------
 * INDICE DE FUNCIONES Y ESTRUCTURA
 * ------------------------------------------------------------------
 * 1. Context Creation .................... Creación del contexto
 * 2. Custom Hook (useModal) .............. Hook para usar el contexto
 * 3. Provider (ModalProvider) ............ Componente proveedor
 *    - State ............................. Estados para modales (confirmación e info)
 *    - showConfirm ....................... Mostrar modal de confirmación
 *    - hideConfirm ....................... Ocultar modal de confirmación
 *    - showInfo .......................... Mostrar modal informativo
 *    - hideInfo .......................... Ocultar modal informativo
 * ------------------------------------------------------------------
 */

/**
 * ------------------------------------------------------------------
 * 1. Context Creation
 * ------------------------------------------------------------------
 */
const ModalContext = createContext();

/**
 * ------------------------------------------------------------------
 * 2. Custom Hook (useModal)
 * ------------------------------------------------------------------
 */
export const useModal = () => {
    const context = useContext(ModalContext);
    if (!context) {
        throw new Error('useModal must be used within a ModalProvider');
    }
    return context;
};

/**
 * ------------------------------------------------------------------
 * 3. Provider (ModalProvider)
 * ------------------------------------------------------------------
 */
export const ModalProvider = ({ children }) => {
    // State
    // Estado para modal de confirmación
    const [confirmState, setConfirmState] = useState({
        show: false,
        title: '',
        message: '',
        confirmLabel: 'Confirmar',
        cancelLabel: 'Cancelar',
        confirmVariant: 'primary',
        onConfirm: null,
    });

    // Estado para modal informativo
    const [infoState, setInfoState] = useState({
        show: false,
        title: '',
        message: '',
        closeLabel: 'Cerrar'
    });

    // showConfirm
    const showConfirm = useCallback((options) => {
        setConfirmState({
            show: true,
            title: options.title || 'Confirmar',
            message: options.message || '',
            confirmLabel: options.confirmLabel || 'Confirmar',
            cancelLabel: options.cancelLabel || 'Cancelar',
            confirmVariant: options.confirmVariant || 'primary',
            onConfirm: options.onConfirm,
        });
    }, []);

    // hideConfirm
    const hideConfirm = useCallback(() => {
        setConfirmState((prev) => ({ ...prev, show: false }));
    }, []);

    // showInfo
    const showInfo = useCallback((options) => {
        setInfoState({
            show: true,
            title: options.title || 'Información',
            message: options.message || '',
            closeLabel: options.closeLabel || 'Cerrar'
        });
    }, []);

    // hideInfo
    const hideInfo = useCallback(() => {
        setInfoState((prev) => ({ ...prev, show: false }));
    }, []);

    return (
        <ModalContext.Provider value={{
            showConfirm,
            hideConfirm,
            confirmState,
            showInfo,
            hideInfo,
            infoState
        }}>
            {children}
        </ModalContext.Provider>
    );
};
